import { message, Radio, Select } from "antd";
import type { Port, ShippingPriceIndex, ShippingServiceProviderConfig } from "@zhihaoscm/entity";
import { PORT_TYPE } from "@zhihaoscm/meta";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { portServcie } from "@/api/portService";

export type ProviderCard = ShippingServiceProviderConfig & { pictureTextInfo: ShippingServiceProviderConfig["pictureTextInfo"] & { path?: string } };

/** Vue commonGoods.vue 的常用货物胶囊。 */
export function ShipCommonGoods({ options, onSelect }: { options: string[]; onSelect: (value: string) => void }) {
    return <div className="ship-common-goods-box">{options.map(item => <button key={item} type="button" onClick={() => onSelect(item)}>{item}</button>)}</div>;
}

/** Vue freightPlatformRadio.vue 的图文服务商选择卡。 */
export function ShipProviderRadio({ options, value, onChange }: { options: ProviderCard[]; value?: number; onChange: (value: number) => void }) {
    return <div className="ship-provider-radio">{options.map(item => <button key={item.businessEnumCode} type="button" onClick={() => onChange(item.businessEnumCode)}>
        <span>{item.pictureTextInfo.path ? <img alt="" src={item.pictureTextInfo.path} /> : null}<span><strong>{item.pictureTextInfo.remark}</strong><small>{item.pictureTextInfo.text}</small></span></span>
        <Radio checked={value === item.businessEnumCode} />
    </button>)}</div>;
}

/** Vue paymentIntroduction.vue 的无标题图片弹窗。 */
export function ShipPaymentIntroduction({ onClose }: { onClose: () => void }) {
    return <div className="ship-payment-introduction" role="dialog"><img alt="服务商垫付介绍" src="https://static.zhihaoscm.cn/customer-admin/lian-yun-ad.png" /><button aria-label="关闭服务商垫付介绍" type="button" onClick={onClose}><i className="iconfont icon-guanbi" /></button></div>;
}

type RemotePortValue = number | number[];

type ShipRemotePortSelectProps = {
    value?: RemotePortValue;
    placeholder?: string;
    popupClassName?: string;
    types?: number[];
    mode?: "multiple";
    maxCount?: number;
    maxSearchLength?: number;
    onChange?: (value: RemotePortValue | undefined, port: Port | Port[] | undefined) => void;
};

/** 转义搜索词，避免用户输入被当作正则表达式。 */
function escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 按 Vue useTextRender 的规则渲染关键字高亮。 */
function renderHighlightedText(text: string, keyword: string) {
    if (!keyword) return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(keyword)})`, "ig"));
    return parts.map((part, index) => part.toLowerCase() === keyword.toLowerCase()
        ? <span className="highlight" key={`${part}-${index}`}>{part}</span>
        : part);
}

/** Vue remotePortSelect 的 React 等价实现：远程分页、精确回显、已选缓存和完整条目回传。 */
export function ShipRemotePortSelect({
    value,
    placeholder = "请输入码头名称或城市选择",
    popupClassName,
    types,
    mode,
    maxCount = 10,
    maxSearchLength,
    onChange
}: ShipRemotePortSelectProps) {
    const [options, setOptions] = useState<Port[]>([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState("");
    const keywordRef = useRef("");
    const pageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const loadingRef = useRef(false);
    const requestRef = useRef(0);
    // 与 Vue useSelectCache 一致，防止远程翻页或切换关键字后已选项丢失。
    const selectedCacheRef = useRef(new Map<number, Port>());

    /** 将接口条目写入已选缓存，并按 id 去重合并列表。 */
    const mergePorts = useCallback((current: Port[], incoming: Port[], reset: boolean) => {
        incoming.forEach(port => selectedCacheRef.current.set(port.id, port));
        const merged = reset ? incoming : [...current, ...incoming];
        return Array.from(new Map(merged.map(port => [port.id, port])).values());
    }, []);

    /** 与 Vue loadData 一致：每页十条，搜索时重置，滚动时追加。 */
    const load = useCallback(async (keyword: string, reset: boolean, portIds: number[] = []) => {
        if (loadingRef.current && !reset) return;
        const requestId = ++requestRef.current;
        const page = reset ? 1 : pageRef.current;
        loadingRef.current = true;
        setLoading(true);
        try {
            const params: { page: number; size: number; portName?: string; portIds?: number[]; types?: number[] } = { page, size: 10 };
            // Vue 只在空字符串时移除 portName；精确回显则通过 portIds 查询。
            if (keyword !== "") params.portName = keyword;
            if (portIds.length) params.portIds = portIds;
            if (types) params.types = types;
            const result = await portServcie.pagingSelector(params);
            if (requestId !== requestRef.current) return;
            const list = result.list ?? [];
            setOptions(current => mergePorts(current, list, reset));
            keywordRef.current = keyword;
            pageRef.current = page + 1;
            hasMoreRef.current = Number(result.total ?? 0) > page * 10;
        } finally {
            if (requestId === requestRef.current) {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    }, [mergePorts, types]);

    /** Vue remoteMethod：清空精确查询条件并从第一页搜索。 */
    const search = useCallback((keyword: string) => {
        // 先同步关键字，确保本次接口结果首次渲染时即可高亮，而不是等下一次重渲染。
        keywordRef.current = keyword;
        setKeyword(keyword);
        pageRef.current = 1;
        hasMoreRef.current = true;
        void load(keyword, true);
    }, [load]);

    /** 表单编辑回显时复刻 remoteSearchIds，按 id 精确补齐候选标签。 */
    useEffect(() => {
        const ids = (Array.isArray(value) ? value : value == null ? [] : [value])
            .filter(id => !selectedCacheRef.current.has(id));
        if (!ids.length) return;
        pageRef.current = 1;
        hasMoreRef.current = true;
        void load("", true, ids);
    }, [load, value]);

    const selectOptions = useMemo(() => {
        const merged = [...options];
        const selectedIds = Array.isArray(value) ? value : value == null ? [] : [value];
        selectedIds.forEach(id => {
            const cached = selectedCacheRef.current.get(id);
            if (cached && !merged.some(port => port.id === id)) merged.push(cached);
        });
        return merged;
    }, [options, value]);

    /** 码头显示所属港口，泊位显示所属码头，港口不显示父级。 */
    const getParentName = (port: Port) => port.type === PORT_TYPE.PORT.code
        ? port.harborShortName
        : port.type === PORT_TYPE.BERTH.code ? port.portShortName : "";

    /** 解析完整选中项并保持 Vue change 事件的数据形态。 */
    const changeValue = (next: RemotePortValue | undefined) => {
        if (Array.isArray(next) && maxCount && next.length > maxCount) {
            void message.warning(`最多只能选择${maxCount}个`);
            return;
        }
        if (next == null || (Array.isArray(next) && !next.length)) {
            onChange?.(next, Array.isArray(next) ? [] : undefined);
            return;
        }
        if (Array.isArray(next)) {
            const selected = next.map(id => selectOptions.find(port => port.id === id) ?? selectedCacheRef.current.get(id)).filter(Boolean) as Port[];
            selected.forEach(port => selectedCacheRef.current.set(port.id, port));
            onChange?.(next, selected);
            return;
        }
        const selected = selectOptions.find(port => port.id === next) ?? selectedCacheRef.current.get(next);
        if (selected) selectedCacheRef.current.set(selected.id, selected);
        onChange?.(next, selected);
    };

    return <Select
        className="remote-port-select"
        showSearch
        allowClear
        // Vue el-select 不做虚拟化；关闭虚拟列表后选项高度、滚动触底和旧版保持一致。
        virtual={false}
        mode={mode}
        maxCount={mode === "multiple" ? maxCount : undefined}
        filterOption={false}
        value={value}
        placeholder={placeholder}
        classNames={{ popup: { root: ["remote-port-select-popper", popupClassName].filter(Boolean).join(" ") } }}
        popupMatchSelectWidth={350}
        loading={loading}
        notFoundContent={<div className="remote-port-select-empty">{loading ? "加载中" : "暂无数据"}</div>}
        // 查船页支持原生全屏；下拉层必须挂到全屏容器内，否则浏览器会把 body Portal 隐藏在全屏层后。
        getPopupContainer={trigger => (trigger as HTMLElement).closest(".find-location-page") ?? document.body}
        options={selectOptions.map(port => ({
            label: port.shortName || port.name,
            value: port.id,
            // 将渲染依赖放进 option 数据，避免 Select 内部缓存沿用上一次搜索词的展示。
            port,
            searchKeyword: keyword
        }))}
        optionRender={option => {
            const port = option.data.port as Port | undefined;
            if (!port) return option.label;
            const parentName = getParentName(port);
            return <div className="remote-port-select-option">
                <span className="remote-port-select-kind"><i className={`iconfont ${port.type === PORT_TYPE.HARBOR.code ? "icon-jetty" : "icon-berth"}`} />{PORT_TYPE.getIntroByCode(port.type)}</span>
                <span className="remote-port-select-name"><strong>{renderHighlightedText(port.shortName || port.name, option.data.searchKeyword as string)}</strong>{parentName ? <small>{parentName}</small> : null}</span>
            </div>;
        }}
        onOpenChange={open => {
            // 输入会自动打开下拉层；此时已有搜索请求时不能再用空关键字覆盖它。
            if (open && !options.length && !loadingRef.current && requestRef.current === 0) search("");
        }}
        onPopupScroll={event => {
            // Ant Design 将真实滚动容器放在 event.target；与 Vue 原实现保持一致读取 target。
            const target = event.target as HTMLElement;
            if (!loadingRef.current && hasMoreRef.current && target.scrollHeight - target.scrollTop - target.clientHeight <= 20) void load(keywordRef.current, false);
        }}
        onSearch={search}
        onInputKeyDown={event => {
            if (!maxSearchLength) return;
            const input = event.currentTarget;
            const editingKey = event.metaKey || event.ctrlKey || ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"].includes(event.key);
            if (!editingKey && event.key.length === 1 && input.value.length >= maxSearchLength) {
                event.preventDefault();
                void message.warning(`最多输入${maxSearchLength}个字符`);
            }
        }}
        onChange={changeValue}
    />;
}

/** Vue priceIndexDetail.vue 的航线与分吨位参考价卡片。 */
export function ShipPriceIndexCard({ value }: { value: ShippingPriceIndex }) {
    const prices = value.prices;
    return <div className="ship-price-index-card"><div><span>{value.date}</span><strong>{value.shipRouteSource}</strong><i className="iconfont icon-arrow" /><strong>{value.shipRouteDestination}</strong></div><section>
        {prices.sixToNineThousandPrice ? <p><strong>￥{Number(prices.sixToNineThousandPrice).toFixed(2)}/吨</strong><span>0.6-0.9万吨位</span></p> : null}
        {prices.tenThousandPrice ? <p><strong>￥{Number(prices.tenThousandPrice).toFixed(2)}/吨</strong><span>1万吨位</span></p> : null}
        {prices.fifteenUpThousandPrice ? <p><strong>￥{Number(prices.fifteenUpThousandPrice).toFixed(2)}/吨</strong><span>1.5万吨位以上</span></p> : null}
    </section></div>;
}
